<template>
  <b-navbar toggleable type="light" variant="light" class="p-3">
    <div class="container">
      <b-navbar-brand class="title">
        {{ t("titleInvoice") }}
      </b-navbar-brand>
    </div>
  </b-navbar>
  <div v-if="loader" class="message-container-align">
    <b-spinner
      style="width: 6rem; height: 6rem; color: #003778"
      label="Large Spinner"
    ></b-spinner>
  </div>
  <div
    v-if="invalidIDFlag || errorFlag || unAuthorizedFlag"
    class="container-fluid message-container-align"
  >
    <h2 v-if="unAuthorizedFlag">{{ t("form.unauthorized") }}</h2>
    <h2 v-if="invalidIDFlag">{{ t("form.invalidrequest") }}</h2>
    <h2 v-if="errorFlag && !unAuthorizedFlag">
      {{ t("form.unabletoprocesstherequest") }}
    </h2>
  </div>

  <div
    v-if="!loader && !invalidIDFlag && !errorFlag && !unAuthorizedFlag"
    class="container p-3"
    style="min-height: 363px"
  >
    <p>
      {{ t("form.expirydaysmessageInvoice") }}
      <br />
      {{ t("form.expirydaysmessageInvoiceDay") }}
      <a href="">{{ expiryDays }} {{ t("form.days") }}</a
      >.
    </p>
    <div class="spacer15"></div>

    <b-form-group label-for="otp-input" label-class="custom-label">
      <label class="custom-label" :state="otpState">{{
        t("otpValidate.verificationCode")
      }}</label>
      <div class="spacer10"></div>

      <div class="d-flex align-items-start">
        <b-col sm="4" class="otp-input-container">
          <b-form-input
            id="otp-input"
            v-model="otp"
            maxlength="6"
            :placeholder="t('otpValidate.verificationCode')"
            :state="otpState"
            class="custom-placeholder"
            required
          />
          <b-form-invalid-feedback :state="otpState" class="d-block">
            {{ otpErrorMessage }}
          </b-form-invalid-feedback>

          <div v-if="otpSentFlag" class="text-success mt-2 small">
            <i class="fas fa-check-circle me-1"></i>
            {{ t("otpValidate.codeSent") }}
          </div>

          <div
            v-if="otpErrorFlag && otpPopUpErrorMessage"
            class="text-danger mt-2 small"
          >
            <i class="fas fa-exclamation-circle me-1"></i>
            {{ otpPopUpErrorMessage }}
          </div>

          <div class="spacer10"></div>
          <div class="d-flex align-items-center">
            <a
              class="request-code-link"
              :class="{ 'disabled-link': counting }"
              :aria-disabled="counting"
              :tabindex="counting ? -1 : 0"
              @click.prevent="!counting && requestVerificationCode()"
            >
              {{ t("otpValidate.requestVerificationCode") }}
            </a>

            <!-- Countdown moved next to the request code link -->
            <small
              v-if="counting"
              class="ms-3 text-muted small countdown-inline"
            >
              {{ t("otpValidate.codeExpireIn") }}:
              <i class="fas fa-clock me-1"></i>
              <vue-countdown
                :time="counDownTime"
                format="mm:ss"
                @end="onCountdownEnd"
                v-slot="{ minutes, seconds }"
              >
                {{ String(minutes).padStart(2, "0") }}:{{
                  String(seconds).padStart(2, "0")
                }}
              </vue-countdown>
            </small>
          </div>
          <!-- <a class="request-code-link" @click.prevent="requestVerificationCode">
             {{ t("otpValidate.requestVerificationCode") }}
           </a> -->
        </b-col>
      </div>
    </b-form-group>

    <!-- Captcha: same logic & component as SubscriberPdfForm.vue -->
    <b-form-group
      :label="t('form.captcha')"
      label-for="captcha-input"
      class="custom-label mt-3"
    >
      <b-col sm="4">
        <b-form-input
          id="captcha-input"
          type="text"
          :placeholder="t('form.entercaptcha')"
          v-model="captchaInput"
          :state="captchaState"
          class="custom-placeholder"
          required
        />
        <b-form-invalid-feedback
          v-if="captchaError"
          :state="captchaState"
          class="d-block"
        >
          {{ captchaErrorMessage }}
        </b-form-invalid-feedback>
      </b-col>

      <div class="spacer10"></div>
      <Recaptcha @captcha-updated="handleCaptchaUpdate" />
    </b-form-group>

    <!-- Action button -->
    <div class="d-flex align-items-center mt-3">
      <b-button
        variant="primary"
        class="next-button me-3"
        @click="onOtpNext"
        :disabled="!isFormReady"
      >
        {{ t("otpValidate.download") }}
      </b-button>

      <!-- countdown removed from here (moved inline next to request link) -->
    </div>

    <div class="spacer30"></div>

    <span id="previousDownloads" class="txtSize15">
      {{ t("download.previousdownloads") }}: {{ downloadCount }}
    </span>
    <div class="spacer5"></div>
    <span id="remainingDownloads" class="txtSize15">
      {{ t("download.remainingdownloads") }}: {{ remainingDownloads }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, computed } from "vue";
import VueCountdown from "@chenfengyuan/vue-countdown";
import CryptoJS from "crypto-js";
import router from "@/router";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { generateChecksum } from "../services/generateChecksum";
import { decryptData } from "../services/decryptData";
//import ErrorModal from "./ErrorModal.vue";
import Recaptcha from "./Recaptcha.vue";

const lastOtpErrorCode = ref("");
const otpErrorFlag = ref(false);
const otpSentFlag = ref(false);
const otp = ref("");
const otpState = ref<boolean | null>(null);
const otpError = ref("");
const otpErrorMessage = ref("");
const otpPopUpErrorMessage = ref("");
const expiryDays = computed(() => userDocument?.value?.ExpiryDays);
const secretKey = process.env.VUE_APP_ENCRYPT_DECRYPT_SECRET_KEY;
const JwtSecretKey = process.env.VUE_APP_JWT_KEY;
const errorFlag = ref(false);
const unAuthorizedFlag = ref(false);
const store = useStore();
const loader = ref(true);
const counting = ref(false);
const counDownTime = ref(0);
const { t } = useI18n();
const userDocument = ref<any | null>(null);
const invoiceDetail = ref<any | null>(null);
const downloadCount = computed(() => invoiceDetail?.value?.DownloadCount);
const remainingDownloads = computed(
  () => userDocument?.value?.MaxDownloads - invoiceDetail?.value?.DownloadCount
);
const invalidIDFlag = ref(false);
const baseUrl = process.env.VUE_APP_BASE_API_URL;
const route = useRoute();
const userId = ref(route.query.p as string);

// Captcha state and logic (copied from SubscriberPdfForm.vue)
const captchaInput = ref("");
const captchaText = ref("");
const captchaState = ref<boolean | null>(null);
const captchaError = ref<"empty" | "mismatch" | null>(null);
const handleCaptchaUpdate = (text: string) => {
  captchaText.value = text;
};

// countdown end handler
const onCountdownEnd = () => {
  counting.value = false;
  otpSentFlag.value = false;
  otpErrorFlag.value = true;
  otpPopUpErrorMessage.value = t("otpValidate.timeExpired");
  lastOtpErrorCode.value = "EB005";
};

// build readiness computed (enable button only when basic validation ok)
const isFormReady = computed(() => {
  // quick check to enable button (OTP length 6 + captcha filled)
  return (
    otp.value &&
    otp.value.trim().length === 6 &&
    captchaInput.value &&
    captchaInput.value === captchaText.value &&
    !loader.value
  );
});

const onOtpNext = () => {
  if (validateOtpForm()) {
    validateOtp();
  }
};

// validate OTP form + captcha
watch(otp, () => (otpState.value = null));

const validateOtpForm = () => {
  let isValid = true;
  if (!otp.value || otp.value.trim() === "") {
    otpState.value = false;
    otpError.value = "empty";
    otpErrorMessage.value = t("otpValidate.pleaseEnterOtp");
    isValid = false;
  } else if (otp.value.length < 6) {
    otpState.value = false;
    otpError.value = "invalid";
    otpErrorMessage.value = t("otpValidate.pleaseEnterDigit");
    isValid = false;
  } else {
    otpError.value = "";
    otpErrorMessage.value = "";
  }

  // captcha validation
  captchaState.value = captchaInput.value === captchaText.value;
  if (!captchaInput.value) {
    captchaError.value = "empty";
    captchaState.value = false;
    isValid = false;
  } else if (captchaInput.value !== captchaText.value) {
    captchaError.value = "mismatch";
    captchaState.value = false;
    isValid = false;
  } else {
    captchaError.value = null;
  }

  return isValid;
};

const validateOtp = async () => {
  counting.value = false;
  counDownTime.value = 0;
  try {
    loader.value = true;
    let token = getTokenFromSessionStorage();
    if (!token) {
      token = await getToken(token);
    }
    const encryptedDataValue = {
      userId: userId.value,
      otp: otp.value,
    };
    const checksumString = Object.values(encryptedDataValue).join("-");
    const checksum = generateChecksum(checksumString);
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const response = await axios.post(
      `${baseUrl}/validate`,
      { encryptedDataString },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Checksum": checksum,
        },
      }
    );

    const encryptedData = response.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = response.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(response.data);
    const OtpValidateData = JSON.parse(decryptedText);
    console.log("OTP Validate API Response:", OtpValidateData);
    if (
      receivedChecksum === calculatedChecksum &&
      OtpValidateData.UserId === userId.value
    ) {
      const status = OtpValidateData.Status;
      const otpData = OtpValidateData.OtpData || {};
      const code = otpData.code;

      if (status && status.toLowerCase() === "success") {
        //loader.value = true;
        //otpState.value = true;
        store.commit("setAuth", true);
        setTimeout(() => {
          router.push({
            name: "PDFInvoiceDownloadPage",
            query: { p: userId.value },
          });
        }, 0);
      } else {
        otpSentFlag.value = false;
        otpState.value = false;
        const remainingMs = computeRemainingMs(otpData.nextOtpAt);

        console.log("Time until next OTP (ms):", remainingMs);
        counDownTime.value = remainingMs;
        counting.value = remainingMs > 0;
        loader.value = false;
        switch (code) {
          case "EB004":
            lastOtpErrorCode.value = code;
            otpErrorFlag.value = true;
            otpPopUpErrorMessage.value = t("otpValidate.invalidCode");
            break;
          case "EB005":
            lastOtpErrorCode.value = code;
            otpErrorFlag.value = true;
            otpPopUpErrorMessage.value = t("otpValidate.codeExpired");
            break;
          case "EB006":
            lastOtpErrorCode.value = code;
            otpErrorFlag.value = true;
            otpPopUpErrorMessage.value = t("otpValidate.attemptLocked");
            break;
          default:
            lastOtpErrorCode.value = code;
            otpErrorFlag.value = true;
            otpPopUpErrorMessage.value = t(
              "otpValidate.unabletoprocesstherequest"
            );
            break;
        }
      }
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error) {
    loader.value = false;
    console.error("OTP validation failed", error);
    otpState.value = false;
    otpErrorMessage.value = t("common.unableToProcess");
  }
  // finally {
  //   loader.value = false;
  // }
};

// re-used token, fetch and storage helpers (same as original)
const getInvoiceCount = async (token: any) => {
  const checksumString = `${userId.value}`;
  const checksum = generateChecksum(checksumString);
  try {
    const invoiceCountDetail = await axios.get(
      `${baseUrl}/invoice/count/${userId.value}`,
      {
        headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
      }
    );
    const encryptedData = invoiceCountDetail.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = invoiceCountDetail.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(invoiceCountDetail.data);
    const invoiceData = JSON.parse(decryptedText);
    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value
    ) {
      invoiceDetail.value = invoiceData;
      loader.value = false;
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    console.error("Failed to fetch invoice details:", error);
  }
};

const getToken = async (token: any) => {
  token = "";
  try {
    const encryptedDataValue = {
      UserId: userId.value,
      SecretKey: JwtSecretKey,
      timestamp: new Date().toISOString(),
    };
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const tokenResponse = await axios.post(`${baseUrl}/auth/generate-token`, {
      encryptedDataString,
    });
    token = tokenResponse.data.token;
    setTokenInSessionStorage(token, 23);
    return token;
  } catch (error: any) {
    console.error("Failed to generate token:", error);
    if (error.response) {
      if (error.response.status === 404 || error.response.status === 401) {
        unAuthorizedFlag.value = true;
      } else {
        errorFlag.value = true;
        throw new Error("Failed to generate token. Please try again.");
      }
    }
  }
};

const setTokenInSessionStorage = (token: any, expiresIn: number) => {
  const expirationTime = Date.now() + expiresIn * 3600 * 1000;
  sessionStorage.setItem(`token_${userId.value}`, token);
  sessionStorage.setItem(
    `tokenExpiration_${userId.value}`,
    expirationTime.toString()
  );
};

const getTokenFromSessionStorage = () => {
  const token = sessionStorage.getItem(`token_${userId.value}`);
  const expirationTime = parseInt(
    sessionStorage.getItem(`tokenExpiration_${userId.value}`) || "0"
  );

  if (Date.now() > expirationTime) {
    sessionStorage.removeItem(`token_${userId.value}`);
    sessionStorage.removeItem(`tokenExpiration_${userId.value}`);
    return null;
  }

  return token;
};

/**
 * fetchUserData in OtpValidate:
 * - only fetch invoice metadata (no link-expiry/publicUrl routing here)
 * - HomePage handles expiry & publicUrl routing
 */
const fetchUserData = async () => {
  try {
    loader.value = true;
    let token = getTokenFromSessionStorage();

    if (!token) {
      token = await getToken(token);
    }
    const checksumString = `${userId.value}`;
    const checksum = generateChecksum(checksumString);

    const invoiceResponse = await axios.get(
      `${baseUrl}/invoice/${userId.value}`,
      {
        headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
      }
    );

    const encryptedData = invoiceResponse.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = invoiceResponse.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(invoiceResponse.data);
    const invoiceData = JSON.parse(decryptedText);

    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value
    ) {
      userDocument.value = invoiceData;
      store.commit("setUserID", userDocument.value.Id);
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
    await getInvoiceCount(token);
  } catch (error: any) {
    console.error("Failed to fetch user data", error);
    if (error.response && error.response.status === 404) {
      invalidIDFlag.value = true;
      loader.value = false;
      throw new Error("User ID not found. Please check the ID and try again.");
    } else {
      errorFlag.value = true;
      loader.value = false;
      throw new Error("Failed to fetch user data. Please try again later.");
    }
  } finally {
    loader.value = false;
  }
};

onMounted(() => {
  fetchUserData();
});
// separate helper to compute remaining milliseconds until target time
const computeRemainingMs = (timestamp: string): number => {
  // API Timestamp "2025-11-20T22:32:55,741-05:00";

  if (!timestamp) return 0;

  // Normalize timestamp: replace first comma after seconds with a dot so JS can parse milliseconds
  const fixedTimestamp = timestamp.replace(",", ".");

  const targetDate = new Date(fixedTimestamp);
  if (isNaN(targetDate.getTime())) {
    console.warn("Failed to parse OTP timestamp:", timestamp);
    return 0;
  }

  const now = new Date();
  return Math.max(0, targetDate.getTime() - now.getTime());
};
const requestVerificationCode = async () => {
  loader.value = true;
  otpErrorFlag.value = false;
  otp.value = "";
  otpState.value = null;
  captchaInput.value = "";
  captchaState.value = null;
  const checksumString = `${userId.value}`;
  const checksum = generateChecksum(checksumString);
  let token = getTokenFromSessionStorage();
  if (!token) {
    token = await getToken(token);
  }
  try {
    const otpDetails = await axios.get(`${baseUrl}/generate/${userId.value}`, {
      headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
    });
    const encryptedData = otpDetails.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = otpDetails.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(otpDetails.data);
    const otpData = JSON.parse(decryptedText);
    if (
      receivedChecksum === calculatedChecksum &&
      otpData.UserId === userId.value
    ) {
      if (otpData.Status.toLowerCase() === "success") {
        loader.value = false;
        otpSentFlag.value = true;
        const est = new Date().toLocaleString("en-US", {
          timeZone: "America/New_York",
        });
        console.log("OTP next at (EST):", otpData.OtpData.nextOtpAt);
        console.log("OTP sent at (EST):", est);

        const remainingMs = computeRemainingMs(otpData.OtpData.nextOtpAt);

        console.log("Time until next OTP (ms):", remainingMs);
        counDownTime.value = remainingMs;
        counting.value = remainingMs > 0;
      } else {
        otpErrorMessage.value = otpData.OtpData.errorReason;
        loader.value = false;
        otpErrorFlag.value = true;
        return;
      }
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    console.error("Failed to fetch Otp details:", error);
  }
};
</script>

<script lang="ts">
export default {
  name: "OtpValidateComponent",
};
</script>

<style scoped>
@media screen and (max-width: 767px) {
  .title {
    font-size: 20px !important;
    margin-left: -15px;
  }
}
.title {
  font-family: "bellslim_mediumregular", Helvetica, Arial, sans-serif;
  font-size: 25px;
  font-weight: bold;
}
.spacer30 {
  height: 30px;
}
.spacer15 {
  height: 15px;
}
.spacer10 {
  height: 10px;
}

.custom-label {
  color: #555;
  font-weight: bold;
  font-size: 16px;
  font-family: "Helvetica", Arial, sans-serif;
}

.next-button {
  color: #fff;
  background-color: #003778;
  border: 2px solid #003778;
  font-size: 15px;
  padding: 10px 32px;
  text-align: center;
  cursor: pointer;
  border-radius: 12px;
  line-height: 1;
  --bs-btn-hover-bg: #003778;
}

/* disabled appearance for next/download button */
.next-button:disabled,
.next-button[disabled],
.next-button.disabled {
  background-color: #babec2 !important;
  border-color: #babec2 !important;
  color: #ffffff !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

.message-container-align {
  min-height: 363px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-placeholder::placeholder {
  font-size: 12.5px;
}

.otp-box {
  border: 1px solid #ccc;
  padding: 1rem;
  border-radius: 8px;
  max-width: 700px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.request-code-link {
  display: inline-block;
  color: #00549a;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
}

/* disabled appearance/behaviour when counting is active */
.disabled-link {
  color: #9aa6b3;
  cursor: default;
  pointer-events: none;
  text-decoration: none;
  opacity: 0.7;
  text-decoration: underline;
}

.txtSize15 {
  font-size: 15px;
}

.countdown-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
